// Data
import { useResetPasswordStore } from '@stores/index';
// Utils
import { LinersApiPaths } from '@/src/utils';
import { getSecondsToTimestamp } from '@helpers/index';
import text from '@translations/en.json';
import { config } from '@utils/env';
import axios from 'axios';

/**
 * Custom hook for sending reset password emails and retrieving the time to the next email retry.
 *
 * @returns An object containing functions for sending reset password emails and retrieving the time to the next email retry.
 */
export const useSendResetPassword = () => {
  const { setUserNextTry, usersTimeToNextTry } = useResetPasswordStore();

  /**
   * Sends a reset password email to the provided username.
   *
   * @param username - The username to send the reset password email to.
   * @throws An error if the username does not exist or if the user's password change is locked.
   */
  const sendResetPasswordEmail = async (username: string) => {
    try {
      await axios.post(`${config?.services?.apiGateway}${LinersApiPaths.RequestForgotPassword}`, {
        username,
      });
      setUserNextTry(username);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        switch (status) {
          case 403:
            throw new Error(text.password_locked);
          case 404:
            throw new Error(text.user_not_found);
          default:
            throw new Error(text.unexpected_error);
        }
      }
    }
  };

  /**
   * Retrieves the time to the next email retry for a given user.
   *
   * @param username - The username for which to retrieve the time to the next retry.
   * @returns The time to the next email retry in seconds.
   */
  const getTimeToNextMail = (username: string) => getSecondsToTimestamp(usersTimeToNextTry[username] ?? Date.now());

  return { sendResetPasswordEmail, getTimeToNextMail };
};
